import React, { useEffect, useRef } from "react";
import { FaEnvelope, FaGithub, FaLinkedin } from "react-icons/fa";
import { siteContent, socialLinks } from "../content/site";
import useReducedMotion from "../hooks/useReducedMotion";
import "./ContactSection.css";

const RESTING_POINTER = { x: 50, y: 124 };
const GLOW_FADE_MS = 220;
const ACTIVE_REVEAL_OPACITY = 1;
const ACTIVE_LIGHT_GLOW_OPACITY = 0.82;
const ACTIVE_DARK_GLOW_OPACITY = 1;
const POINTER_BOUNDS = {
  minX: -6,
  maxX: 106,
  minY: -6,
  maxY: 106,
} as const;

const ContactSection: React.FC = () => {
  const { contact, person } = siteContent;
  const sectionRef = useRef<HTMLElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const resetTimeoutRef = useRef<number | null>(null);
  const pointerRef = useRef(RESTING_POINTER);
  const prefersReducedMotion = useReducedMotion();
  const linkedInLink = socialLinks.find((link) => link.platform === "LinkedIn");
  const gitHubLink = socialLinks.find((link) => link.platform === "GitHub");

  useEffect(
    () => () => {
      if (animationFrameRef.current !== null) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }

      if (resetTimeoutRef.current !== null) {
        window.clearTimeout(resetTimeoutRef.current);
      }
    },
    [],
  );

  const commitPointerPosition = () => {
    const section = sectionRef.current;

    if (!section) {
      animationFrameRef.current = null;
      return;
    }

    section.style.setProperty("--contact-glow-x", `${pointerRef.current.x}%`);
    section.style.setProperty("--contact-glow-y", `${pointerRef.current.y}%`);
    animationFrameRef.current = null;
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (prefersReducedMotion) {
      return;
    }

    const section = sectionRef.current;

    if (!section) {
      return;
    }

    if (resetTimeoutRef.current !== null) {
      window.clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }

    const bounds = section.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;

    pointerRef.current = {
      x: Math.min(POINTER_BOUNDS.maxX, Math.max(POINTER_BOUNDS.minX, x)),
      y: Math.min(POINTER_BOUNDS.maxY, Math.max(POINTER_BOUNDS.minY, y)),
    };

    section.style.setProperty("--contact-reveal-opacity", `${ACTIVE_REVEAL_OPACITY}`);
    section.style.setProperty("--contact-light-glow-opacity", `${ACTIVE_LIGHT_GLOW_OPACITY}`);
    section.style.setProperty("--contact-dark-glow-opacity", `${ACTIVE_DARK_GLOW_OPACITY}`);

    if (animationFrameRef.current !== null) {
      return;
    }

    animationFrameRef.current = window.requestAnimationFrame(commitPointerPosition);
  };

  const handlePointerLeave = () => {
    if (prefersReducedMotion) {
      return;
    }

    const section = sectionRef.current;

    if (!section) {
      return;
    }

    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    section.style.setProperty("--contact-reveal-opacity", "0");
    section.style.setProperty("--contact-light-glow-opacity", "0");
    section.style.setProperty("--contact-dark-glow-opacity", "0");

    if (resetTimeoutRef.current !== null) {
      window.clearTimeout(resetTimeoutRef.current);
    }

    resetTimeoutRef.current = window.setTimeout(() => {
      pointerRef.current = RESTING_POINTER;
      commitPointerPosition();
      resetTimeoutRef.current = null;
    }, GLOW_FADE_MS);
  };

  return (
    <section
      ref={sectionRef}
      className="contact-section"
      id={contact.sectionId}
      onPointerLeave={prefersReducedMotion ? undefined : handlePointerLeave}
      onPointerMove={prefersReducedMotion ? undefined : handlePointerMove}
      style={
        {
          "--contact-glow-x": `${RESTING_POINTER.x}%`,
          "--contact-glow-y": `${RESTING_POINTER.y}%`,
          "--contact-reveal-opacity": "0",
          "--contact-light-glow-opacity": "0",
          "--contact-dark-glow-opacity": "0",
        } as React.CSSProperties
      }
    >
      <div aria-hidden="true" className="contact-section__background">
        <div className="contact-section__grid contact-section__grid--base" />
        <div className="contact-section__grid contact-section__grid--reveal" />
        <div className="contact-section__glow" />
        <div className="contact-section__top-fade" />
      </div>

      <div className="section-shell contact-section__shell">
        <div className="contact-section__container">
          <div className="contact-card">
            <div aria-hidden="true" className="contact-card__surface" />
            <div aria-hidden="true" className="contact-card__highlight" />
            <div className="contact-card__content">
              <h2 className="contact-card__title">
                {contact.title}
              </h2>
              {contact.description ? (
                <p className="contact-card__description">
                  {contact.description}
                </p>
              ) : null}

              <div className="contact-card__actions">
                <a
                  href={`mailto:${person.email}`}
                  className="contact-button contact-button--primary"
                >
                  <FaEnvelope />
                  Email Alexandr
                </a>

                {linkedInLink ? (
                  <a
                    href={linkedInLink.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-button contact-button--secondary contact-button--linkedin"
                  >
                    <FaLinkedin />
                    LinkedIn
                  </a>
                ) : null}

                {gitHubLink ? (
                  <a
                    href={gitHubLink.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-button contact-button--secondary contact-button--github"
                  >
                    <FaGithub />
                    GitHub
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
